'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, Icon, Input, showToast, Tooltip } from '@repo/ui';
import {
  DataTable,
  DataTablePaginatedResponse,
  type DataTableColumn,
} from '@xtnd/web/shared/components/DataTable';

import { PreviewEmailModal } from './PreviewEmailModal';
import { dateUtil, numberUtil } from '@xtnd/web/shared/utils/format';
import { useMatterARContext } from '../common/MatterARContext';
import MatterDetailDrawer from '../matter-detail/MatterDetailDrawer';
import { reportingEmailQueueApi } from '../../api/reportingEmailQueueApi';
import { useTableParams } from '../../hooks/useTableParams';
import ActionsToolbarEmailQueue, { EmailQueueActions } from './ActionsToolbarEmailQueue';
import { ArMatterChildTable } from './ArMatterChildTable';
import { DAYS_PAST_DUE } from '../../constants';
import {
  emailGroupByAttorneyOriginalOptions,
  emailGroupByAttorneyResponseOptions,
  emailGroupByOptions,
  getRowUniqueKey,
  transformDataAttorney,
  populateEmailMessage,
} from './utils';
import { getAgingBucketBounds } from './utils/agingBuckets';
import { ArEmailGroupByOption, CLIENT_TYPE_FILTER_ALL } from '@repo/types';
import { ArClientsTable } from './ArClientsTable';
import { RemoveEmailModal } from './RemoveEmailModal';
import { SIRBadge } from '../SIRBadge';
import { useEmailQueue, useEmailQueueSubItems } from '../../queries';
import {
  ArEmailQueueFilter,
  ArEmailQueueItem,
  ArEmailQueueMatter,
} from '../../types';
import { useArEmailCollectionsStore } from '@xtnd/web/shared/stores';
import ArReportingReload from '../common/ArReportingReload';

const INITIAL_FILTERS: ArEmailQueueFilter = {
  groupBy: ArEmailGroupByOption.CLIENT,
  search: '',
};

export default function EmailQueueTable() {
  const { tableParams, setParams } = useTableParams({
    page: 1,
    limit: 50,
    sorts: { maxDaysPastDue: 'desc' },
    filters: INITIAL_FILTERS,
  });
  const filters = (tableParams.filters as ArEmailQueueFilter) ?? INITIAL_FILTERS;
  const groupBy = filters.groupBy;
  const { data, error, refetch, isFetching } = useEmailQueue(
    { ...tableParams },
    { enabled: true }
  );
  const {
    clients,
    matters,
    selectedKeys,
    isSending,
    setSelectedKeys,
    setMatters,
    setClients,
    setIsSending,
    updateMatters,
  } = useArEmailCollectionsStore();

  const [emailSelected, setEmailSelected] = useState<ArEmailQueueMatter | null>(null);
  const [isPreviewEmail, setIsPreviewEmail] = useState<boolean>(false);
  const [removeEmailSelected, setRemoveEmailSelected] = useState<ArEmailQueueMatter | null>(null);
  const [removeEmailsSelected, setRemoveEmailsSelected] = useState<
    { matterId: string | undefined; clientId: string | undefined }[] | []
  >([]);
  const [searchLocal, setSearchLocal] = useState<string>('');
  const [searchLocalChildTable, setSearchLocalChildTable] = useState<string>('');
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { openMatterDrawer } = useMatterARContext();

  const subItemsEntityId =
    groupBy === ArEmailGroupByOption.NONE ? null : selectedEntityId;
  const { data: subItemsData, isFetching: isFetchingSubItems } = useEmailQueueSubItems(
    subItemsEntityId,
    groupBy
  );

  useEffect(() => {
    if (subItemsData?.data) {
      if (groupBy === ArEmailGroupByOption.CLIENT) {
        setMatters(subItemsData?.data as any);
      } else if (groupBy === ArEmailGroupByOption.ORIGINATING) {
        setClients(transformDataAttorney(subItemsData?.data ?? []));
      } else if (groupBy === ArEmailGroupByOption.RESPONSIBLE) {
        setClients(transformDataAttorney(subItemsData?.data ?? []));
      }
    }
  }, [subItemsData, groupBy, setMatters, setClients]);

  const itemsSelected = useMemo(() => {
    const currentData =
      groupBy === ArEmailGroupByOption.NONE
        ? (data?.data ?? [])
        : (matters?.length ? matters : (data?.data ?? []));
    return currentData.filter(item => selectedKeys.includes(getRowUniqueKey(item)));
  }, [selectedKeys, matters, data?.data, clients, groupBy]);

  const noEmailItem = useMemo(() => {
    return itemsSelected.filter(
      (d: ArEmailQueueItem | ArEmailQueueMatter) => !d.matterAdjusterEmail && !d.clientEmail
    );
  }, [itemsSelected]);

  const isShowMatterList = selectedEntityId && groupBy === ArEmailGroupByOption.CLIENT;
  const isShowClientList = selectedEntityId && groupBy !== ArEmailGroupByOption.CLIENT;

  const handleSendEmail = async (itemsSelected: ArEmailQueueMatter[] | []) => {
    const filteredItems = itemsSelected.filter(d => d?.matterAdjusterEmail || d.clientEmail);
    if (filteredItems.length === 0) {
      showToast.warning(`Email cannot be sent without a recipient.`);
      return;
    }
    setIsSending(true);
    const successList: ArEmailQueueMatter[] = [];
    try {
      for (const item of filteredItems) {
        try {
          await reportingEmailQueueApi.sendArEmail({
            items: [
              {
                matterId: item.matterId,
                clientId: item.clientId,
              },
            ],
          });
          populateEmailMessage({
            error: null,
            email: item?.matterAdjusterEmail || item.clientEmail,
          });
          successList.push(item);
        } catch (error) {
          populateEmailMessage({
            error: error,
            email: item?.matterAdjusterEmail || item.clientEmail,
          });
        }
      }
    } catch (error) {
      populateEmailMessage({
        error: error,
        email: null,
      });
    } finally {
      setIsSending(false);
      if (successList.length > 0) {
        setSelectedKeys([]);
        refetch();
        queryClient.invalidateQueries({ queryKey: ['email-queue-sub-items'] });
      }
    }
  };

  // Mirror the BE list-endpoint behavior: the email-queue list only narrows by
  // aging bucket when `groupBy === NONE`. Gate the drilldown bounds the same way
  // so drawer/preview drilldowns don't filter aggressively while the parent list
  // appears unfiltered (which would visibly reconcile-mismatch the screen).
  const agingBucketBounds = useMemo(
    () =>
      groupBy === ArEmailGroupByOption.NONE && filters.agingBucket
        ? getAgingBucketBounds(filters.agingBucket)
        : {},
    [filters.agingBucket, groupBy]
  );

  const handleClickExternalIcon = (emailIte: ArEmailQueueMatter) => {
    if (emailIte?.matterId && emailIte?.clientId) {
      openMatterDrawer(
        emailIte.matterId,
        emailIte.clientId,
        agingBucketBounds.daysPastDue,
        agingBucketBounds.daysPastDueCap
      );
    }
  };

  const handleRemoveFromEmailQueue = (emailItem: ArEmailQueueMatter) => {
    setRemoveEmailSelected(emailItem);
  };

  const handleNoneRowCheckbox = (
    e: React.ChangeEvent<HTMLInputElement>,
    row: ArEmailQueueItem
  ) => {
    e.stopPropagation();
    const key = getRowUniqueKey(row);
    const next = e?.target?.checked
      ? [...selectedKeys, key]
      : selectedKeys.filter(k => k !== key);
    setSelectedKeys(next);
  };

  const handleNoneSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e?.target?.checked;
    const keys = checked ? (data?.data ?? []).map(ite => getRowUniqueKey(ite)) : [];
    setSelectedKeys(keys);
  };

  const handleBulkRemoveFromEmailQue = (selectedKeys: string[]) => {
    if (selectedKeys.length === 0) {
      return;
    }
    // Look up rows from the selected list rather than parsing the key — IDs are
    // UUIDs with dashes, so a naive split('-') corrupts the values.
    const itemsToBeDelete = itemsSelected
      .filter(item => 'matterId' in item && item.matterId)
      .map(item => ({
        clientId: (item as ArEmailQueueMatter).clientId,
        matterId: (item as ArEmailQueueMatter).matterId,
      }));
    setRemoveEmailsSelected(itemsToBeDelete);
  };

  const handleRemoveEmailModalClose = () => {
    setRemoveEmailSelected(null);
    updateMatters([]);
    setRemoveEmailsSelected([]);
  };

  const handleCloseEmail = () => {
    setEmailSelected(null);
    setIsPreviewEmail(false);
  };

  const handleChangeFilters = (nextFilters: ArEmailQueueFilter) => {
    setSelectedKeys([]);
    const group = nextFilters.groupBy;
    if (group !== filters.groupBy) {
      setSelectedEntityId(null);
      setMatters([]);
      setClients([]);
      setSearchLocal('');
      setSearchLocalChildTable('');
      setEmailSelected(null);
      setIsPreviewEmail(false);

      const currentSorts = tableParams.sorts ?? {};
      const sortKeys = Object.keys(currentSorts);
      const isPreservableSort =
        sortKeys.length === 1 &&
        (sortKeys[0] === 'amountOutstanding' || sortKeys[0] === 'maxDaysPastDue') &&
        (currentSorts[sortKeys[0]] === 'asc' || currentSorts[sortKeys[0]] === 'desc');
      const nextSorts = isPreservableSort ? currentSorts : { maxDaysPastDue: 'desc' as const };

      const resetFilters: ArEmailQueueFilter = {
        ...nextFilters,
        search: '',
        clientType: CLIENT_TYPE_FILTER_ALL,
        agingBucket: undefined,
      };

      setParams({
        ...tableParams,
        page: 1,
        sorts: nextSorts,
        filters: resetFilters,
      });
      return;
    }
    setParams({ ...tableParams, filters: nextFilters });
  };

  const handleRowClicked = useCallback(
    (row: ArEmailQueueItem) => {
      if (groupBy === ArEmailGroupByOption.NONE) {
        setEmailSelected(row as ArEmailQueueMatter);
        setIsPreviewEmail(true);
        return;
      }
      if (groupBy === ArEmailGroupByOption.CLIENT) {
        setSelectedEntityId(row.clientId);
      } else if (groupBy === ArEmailGroupByOption.ORIGINATING) {
        setSelectedEntityId(row.originatingAttorneyId ?? null);
      } else if (groupBy === ArEmailGroupByOption.RESPONSIBLE) {
        setSelectedEntityId(row.responsibleAttorneyId ?? null);
      }
    },
    [groupBy]
  );

  const onBackToClients = () => {
    setSelectedEntityId(null);
    setMatters([]);
    setSelectedKeys([]);
    setSearchLocal('');
  };

  const handleBackToClientInAttorney = () => {
    if (matters.length === 0) {
      setSelectedEntityId(null);
      setClients([]);
    }
    setMatters([]);
    setSearchLocal('');
    setSelectedKeys([]);
  };

  const getDisplayNameByGroupBy = useMemo(() => {
    if (matters?.[0]?.clientName) {
      return matters?.[0]?.clientName ?? '';
    }
    return '';
  }, [matters]);

  const getDisplayNameClient = useMemo(() => {
    if (clients.length > 0) {
      if (groupBy === ArEmailGroupByOption.ORIGINATING && clients) {
        return clients[0]?.originatingAttorneyName;
      } else if (groupBy === ArEmailGroupByOption.RESPONSIBLE && clients) {
        return clients[0]?.responsibleAttorneyName;
      }
    }
    return '';
  }, [clients, groupBy]);

  const tableColumns: Record<string, DataTableColumn<ArEmailQueueItem>[]> = {
    [ArEmailGroupByOption.NONE]: [
      {
        key: 'select',
        header: (
          <input
            type="checkbox"
            className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
            onChange={handleNoneSelectAll}
            checked={(data?.data?.length ?? 0) > 0 && selectedKeys?.length > 0}
          />
        ),
        align: 'left',
        renderCell: row => (
          <input
            type="checkbox"
            className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
            checked={selectedKeys.includes(getRowUniqueKey(row))}
            onChange={e => handleNoneRowCheckbox(e, row)}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onPointerDown={e => e.stopPropagation()}
          />
        ),
      },
      {
        key: 'clientName',
        header: 'Client',
        align: 'left',
        isResizable: true,
        defaultWidth: 200,
        renderCell: row => (
          <Tooltip contentClassName="h-full" content={row?.clientName} position="auto">
            <span className="text-sm text-gray-900 dark:text-gray-100">{row?.clientName}</span>
          </Tooltip>
        ),
      },
      {
        key: 'matterDisplayNumber',
        header: 'Matter',
        align: 'left',
        isResizable: true,
        defaultWidth: 200,
        renderCell: row => (
          <div className="flex items-center gap-2">
            <Tooltip content={row?.matterDisplayNumber ?? ''} position="auto" className="rounded-md">
              <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                {row?.matterDisplayNumber}
              </span>
            </Tooltip>
            {row?.isSIR && <SIRBadge size="sm" />}
          </div>
        ),
      },
      {
        key: 'originatingAttorneyName',
        header: 'Originating',
        align: 'left',
        isResizable: true,
        defaultWidth: 200,
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {row?.originatingAttorneyName}
          </span>
        ),
      },
      {
        key: 'responsibleAttorneyName',
        header: 'Responsible',
        align: 'left',
        isResizable: true,
        defaultWidth: 200,
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {row?.responsibleAttorneyName}
          </span>
        ),
      },
      {
        key: 'amountOutstanding',
        header: 'Amount Outstanding',
        align: 'left',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100 max-w-[6rem]">
            {numberUtil.formatCurrency(row?.amountOutstanding)}
          </span>
        ),
      },
      {
        key: 'maxDaysPastDue',
        header: 'DPD',
        align: 'right',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
              {row?.maxDaysPastDue}
            </div>
          </div>
        ),
      },
      {
        key: 'invoiceNumber',
        header: 'Invoice #',
        align: 'left',
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <Tooltip
            contentClassName="min-w-[10rem] h-full"
            content={row?.invoiceNumbers?.join(', ') || ''}
            position="right"
          >
            <span
              className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light cursor-pointer block"
              onClick={e => {
                e.stopPropagation();
                setEmailSelected(row as ArEmailQueueMatter);
                setIsPreviewEmail(true);
              }}
            >
              {row?.invoiceNumbers?.join(', ')}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'lastFollowUpDate',
        header: 'Last Follow-Up',
        align: 'left',
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <Tooltip
            contentClassName="min-w-[10rem] h-full"
            content={
              (row?.lastFollowUpDate &&
                dateUtil.formatDate(row.lastFollowUpDate, dateUtil.DateFormat.US)) ||
              ''
            }
            position="right"
          >
            <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light max-w-[10rem] truncate block">
              {row?.lastFollowUpDate &&
                dateUtil.formatDate(row.lastFollowUpDate, dateUtil.DateFormat.US)}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'hiExternalLink',
        header: '',
        align: 'center',
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="relative min-w-[3rem] flex items-center justify-center gap-4">
            <div
              className="group flex items-center justify-center p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-xtnd-blue hover:border-xtnd-blue cursor-pointer transition-colors"
              onClick={e => {
                e.stopPropagation();
                handleClickExternalIcon(row);
              }}
            >
              <Icon
                name="HiExternalLink"
                className="text-sm text-xtnd-blue dark:text-xtnd-blue-400 group-hover:text-white"
              />
            </div>
            <div
              className="group flex items-center justify-center p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-red-500 hover:border-red-500 cursor-pointer transition-colors"
              title="Remove from email queue"
              onClick={e => {
                e.stopPropagation();
                handleRemoveFromEmailQueue(row as ArEmailQueueMatter);
              }}
            >
              <Icon name="FaTrash" className="!h-4 !w-4 text-red-500 group-hover:text-white" />
            </div>
          </div>
        ),
      },
    ],
    [ArEmailGroupByOption.CLIENT]: [
      {
        key: 'clientName',
        header: 'Client Name',
        align: 'left',
        isResizable: true,
        defaultWidth: 250,
        renderCell: row => (
          <Tooltip contentClassName="h-full" content={row?.clientName} position="auto">
            <span className="text-sm text-gray-900 dark:text-gray-100">{row?.clientName}</span>
          </Tooltip>
        ),
      },
      {
        key: 'amountOutstanding',
        header: 'Amount Outstanding',
        align: 'left',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100 max-w-[6rem]">
            {numberUtil.formatCurrency(row?.amountOutstanding)}
          </span>
        ),
      },
      {
        key: 'maxDaysPastDue',
        header: 'DPD',
        align: 'right',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
              {row?.maxDaysPastDue}
            </div>
          </div>
        ),
      },
      {
        key: 'countMaster',
        header: 'Matters',
        align: 'left',
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {row?.numOfItems || 0}
          </span>
        ),
      },
      {
        key: 'action',
        header: '',
        align: 'center',
        renderCell: () => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <button>
              <Icon name="FaAngleRight" size="sm" className="opacity-100" />
            </button>
          </div>
        ),
      },
    ],
    [ArEmailGroupByOption.ORIGINATING]: [
      {
        key: 'originatingAttorneyName',
        header: 'Originating Attorney',
        align: 'left',
        isResizable: true,
        defaultWidth: 200,
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {row?.originatingAttorneyName}
          </span>
        ),
      },
      {
        key: 'amountOutstanding',
        header: 'Amount Outstanding',
        align: 'left',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100 max-w-[6rem]">
            {numberUtil.formatCurrency(row?.amountOutstanding)}
          </span>
        ),
      },
      {
        key: 'maxDaysPastDue',
        header: 'DPD',
        align: 'right',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
              {row?.maxDaysPastDue}
            </div>
          </div>
        ),
      },
      {
        key: 'countClients',
        header: 'Clients',
        align: 'left',
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {row?.numOfItems || 0}
          </span>
        ),
      },
      {
        key: 'action',
        header: '',
        align: 'center',
        renderCell: () => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <button>
              <Icon name="FaAngleRight" size="sm" className="opacity-100" />
            </button>
          </div>
        ),
      },
    ],
    [ArEmailGroupByOption.RESPONSIBLE]: [
      {
        key: 'responsibleAttorneyName',
        header: 'Responsible Attorney',
        align: 'left',
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {row?.responsibleAttorneyName}
          </span>
        ),
      },
      {
        key: 'amountOutstanding',
        header: 'Amount Outstanding',
        align: 'left',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100 max-w-[6rem]">
            {numberUtil.formatCurrency(row?.amountOutstanding)}
          </span>
        ),
      },
      {
        key: 'maxDaysPastDue',
        header: 'DPD',
        align: 'right',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
              {row?.maxDaysPastDue}
            </div>
          </div>
        ),
      },
      {
        key: 'countClients',
        header: 'Clients',
        align: 'left',
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {row?.numOfItems || 0}
          </span>
        ),
      },
      {
        key: 'action',
        header: '',
        align: 'center',
        renderCell: () => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <button>
              <Icon name="FaAngleRight" size="sm" className="opacity-100" />
            </button>
          </div>
        ),
      },
    ],
  };

  const getBackToLabelForMatters = () => {
    if (groupBy === ArEmailGroupByOption.CLIENT) return 'Clients';
    if (groupBy === ArEmailGroupByOption.ORIGINATING) return 'Originating attorneys';
    return 'Responsible attorneys';
  };

  const renderHeader = () => (
    <div className="flex flex-col gap-4">
      {selectedEntityId ? (
        <div className="flex justify-start items-center gap-4">
          <Button
            variant="ghost"
            className="flex items-center gap-1 font-medium text-xtnd-dark dark:text-xtnd-light"
            onClick={onBackToClients}
          >
            <Icon name="FaAngleLeft" /> Back to {getBackToLabelForMatters()}
          </Button>
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {`${getDisplayNameByGroupBy} - Matters`}
          </div>
        </div>
      ) : (
        <div className="-mt-6 flex justify-between items-center gap-x-2">
          <p className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">Email Queue</p>
          <ArReportingReload
            isLoading={isFetching}
            refetch={() => {
              setSelectedKeys([]);
              return refetch();
            }}
          />
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        {selectedEntityId ? (
          <div className="relative flex-1 duration-300 max-w-md">
            <Icon
              name="HiSearch"
              size="sm"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
            />
            <Input
              value={searchLocal}
              variant="outlined"
              onChange={e => setSearchLocal(e.target.value)}
              placeholder="Search matter or invoice"
              className="flex h-[38px] w-full px-3 py-2 pl-9 border border-xtnd-light dark:border-xtnd-dark focus:ring-offset-xtnd-blue focus:ring-transparent focus:border-xtnd-blue dark:focus:border-xtnd-blue"
            />
          </div>
        ) : (
          <ActionsToolbarEmailQueue
            isDataTableLoading={isFetching}
            filters={filters}
            onChange={handleChangeFilters}
            options={renderOptionsSelect}
            selectedKeys={selectedKeys}
            itemsSelected={itemsSelected}
            noEmailItem={noEmailItem}
            isSending={isSending}
            onBulkRemove={handleBulkRemoveFromEmailQue}
            onSendEmail={handleSendEmail}
          />
        )}
        {selectedEntityId && selectedKeys && selectedKeys.length > 0 && (
          <EmailQueueActions
            selectedKeys={selectedKeys}
            itemsSelected={itemsSelected}
            noEmailItem={noEmailItem}
            isSending={isSending}
            disabled={isFetching || isFetchingSubItems}
            onBulkRemove={handleBulkRemoveFromEmailQue}
            onSendEmail={handleSendEmail}
          />
        )}
      </div>
    </div>
  );

  const renderOptionsSelect = useMemo(() => {
    if (selectedEntityId) {
      if (groupBy === ArEmailGroupByOption.ORIGINATING) {
        return emailGroupByAttorneyOriginalOptions;
      }
      return emailGroupByAttorneyResponseOptions;
    } else {
      return emailGroupByOptions;
    }
  }, [groupBy, selectedEntityId]);

  const renderHeaderClientsTable = () => {
    const getBackToLabel = () => {
      if (matters.length > 0) return getDisplayNameClient;
      if (groupBy === ArEmailGroupByOption.ORIGINATING) {
        return 'Originating attorneys';
      }
      if (groupBy === ArEmailGroupByOption.RESPONSIBLE) {
        return 'Responsible attorneys';
      }
    };

    return (
      <div className="flex flex-col gap-4 -mt-6">
        <div className="flex justify-start items-center gap-4">
          <Button
            variant="ghost"
            className="flex items-center gap-1 font-medium text-xtnd-dark dark:text-xtnd-light"
            onClick={handleBackToClientInAttorney}
          >
            <Icon name="FaAngleLeft" /> Back to {getBackToLabel()}
          </Button>
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {matters.length === 0
              ? `${getDisplayNameClient} - Clients`
              : `${getDisplayNameByGroupBy} - Matters`}
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 duration-300 max-w-md">
            <Icon
              name="HiSearch"
              size="sm"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
            />
            <Input
              value={searchLocalChildTable}
              variant="outlined"
              onChange={e => setSearchLocalChildTable(e.target.value)}
              placeholder={`Search client`}
              className="flex h-[38px] w-full px-3 py-2 pl-9 border border-xtnd-light dark:border-xtnd-dark focus:ring-offset-xtnd-blue focus:ring-transparent focus:border-xtnd-blue dark:focus:border-xtnd-blue"
            />
          </div>
          {selectedKeys && selectedKeys.length > 0 && (
            <EmailQueueActions
              selectedKeys={selectedKeys}
              itemsSelected={itemsSelected}
              noEmailItem={noEmailItem}
              isSending={isSending}
              disabled={isFetching || isFetchingSubItems}
              onBulkRemove={handleBulkRemoveFromEmailQue}
              onSendEmail={handleSendEmail}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardBody className="p-0">
          {isShowMatterList ? (
            <ArMatterChildTable
              renderHeader={renderHeader}
              onClickExternal={handleClickExternalIcon}
              onRemoveFromEmailQueue={handleRemoveFromEmailQueue}
              onRowClick={row => {
                setEmailSelected(row);
                setIsPreviewEmail(true);
              }}
              searchLocal={searchLocal}
              isLoading={isFetchingSubItems}
            />
          ) : (
            <>
              {isShowClientList ? (
                <ArClientsTable
                  renderHeader={renderHeaderClientsTable}
                  setIsPreviewEmail={setIsPreviewEmail}
                  setEmailSelected={setEmailSelected}
                  onRemoveFromEmailQueue={handleRemoveFromEmailQueue}
                  searchLocal={searchLocalChildTable}
                  isLoading={isFetchingSubItems}
                />
              ) : (
                <DataTable
                  key={`${groupBy}_main_table`}
                  columns={tableColumns[filters.groupBy] ?? []}
                  data={
                    {
                      data: data?.data,
                      total: data?.pagination.total,
                    } as DataTablePaginatedResponse<ArEmailQueueItem>
                  }
                  isLoading={isFetching}
                  error={error}
                  pagination={data?.pagination}
                  onPaginationChange={pagination => {
                    setSelectedKeys([]);
                    setParams({ ...tableParams, ...pagination });
                  }}
                  renderHeader={renderHeader}
                  tableContainerClassName="overflow-y-auto relative"
                  tableClassName="min-w-full divide-y divide-gray-200 relative"
                  stickyHeader={true}
                  pageSizeOptions={[25, 50, 100, 200]}
                  loadingRowCount={5}
                  loadingColumnCount={7}
                  getRowKey={row => getRowUniqueKey(row)}
                  onRowClick={row => {
                    handleRowClicked(row);
                  }}
                  onRetry={() => refetch()}
                  enableColumnResizing
                  columnWidthsStorageKey={`EmailQueue-table-${filters.groupBy}`}
                  onSort={sortBy => {
                    setSelectedKeys([]);
                    setParams({ ...tableParams, sorts: { ...sortBy } });
                  }}
                  sorting={tableParams.sorts}
                />
              )}
            </>
          )}
        </CardBody>
      </Card>
      {emailSelected && (
        <PreviewEmailModal
          open={isPreviewEmail}
          onClose={handleCloseEmail}
          email={emailSelected}
          groupBy={groupBy}
          daysPastDue={agingBucketBounds.daysPastDue}
          daysPastDueCap={agingBucketBounds.daysPastDueCap}
        />
      )}
      <RemoveEmailModal
        open={!!removeEmailSelected}
        onClose={handleRemoveEmailModalClose}
        matter={removeEmailSelected ?? null}
        groupBy={groupBy}
      />
      {/* Bulk remove */}
      <RemoveEmailModal
        open={!!removeEmailsSelected && removeEmailsSelected.length > 0}
        onClose={handleRemoveEmailModalClose}
        matters={matters ?? []}
        itemsToBeDelete={removeEmailsSelected ?? []}
        groupBy={groupBy}
        bulkRemove={!!removeEmailsSelected && removeEmailsSelected.length > 0}
      />
      <MatterDetailDrawer />
    </>
  );
}
